import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components/native';
import { useDispatch, useSelector } from 'react-redux';

import { calcWidth, calcHeight, calcFontSize, deviceWidth, isWeb } from '../../../../../../utils/dimensions';
import Divider from '../../../../../shared/Divider';
import { useTranslation } from 'react-i18next';
import { IS_ANDROID, IS_IOS } from '../../../../../../utils/constants/env';
import { saveIntroSubtitlesLines } from '../../../../../../store/createBite/createBites.actions';
import { introSubtitlesLinesSelector } from '../../../../../../store/createBite/createBite.selectors';
import { ILine } from '../../../../../../types/subtitles';
import ShadowedContainer from '../../../../../ShadowedContainer';

interface IProps {
  isCurrent: boolean;
  line: ILine;
  lineOriginal: ILine;
  onSelect: () => void;
  onChangeLine: (ILine) => void;
  onBlur?: () => void;
}

export const LINE_HEIGHT = 80;
export const LINE_MARGIN = 16;
const localePrefix = 'enhancements';

const Line: React.FC<IProps> = ({ isCurrent, line, lineOriginal, onChangeLine, onSelect, onBlur }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const linesOriginalData = useSelector(introSubtitlesLinesSelector);
  const [inputHeight, setInputHeight] = useState(LINE_HEIGHT);

  const isEdited = useMemo(() => line.line !== lineOriginal.line, [line, lineOriginal]);

  const [lineUpdatedCounter, setLineUpdatedCounter] = useState(0); // save counter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialTextValue = useMemo(() => line.line, [lineUpdatedCounter]); // update on save

  const setLine = useCallback(
    (value) => {
      onChangeLine({
        ...line,
        line: value,
      });
    },
    [onChangeLine, line],
  );

  const handleContentSizeChange = useCallback((event) => {
    setInputHeight(event.nativeEvent.contentSize.height);
  }, []);

  const handleOnBlur = useCallback(() => {
    if (line.line === initialTextValue) {
      return;
    }
    setLineUpdatedCounter(Date.now()); // trigger update of currently saved state
    dispatch(
      saveIntroSubtitlesLines({
        subtitlesId: linesOriginalData.id,
        lines: [
          {
            line: line.line,
            time_line: line.time_line,
            num: line.num,
          },
        ],
      }),
    );
    onBlur();
  }, [line.line, line.time_line, line.num, initialTextValue, dispatch, linesOriginalData.id, onBlur]);

  return (
    <S.Container>
      <ShadowedContainer viewStyle={S.ShadowedContainerStyle} containerViewStyle={S.ShadowedContainerViewStyle}>
        <S.Line height={inputHeight + calcHeight(50)} isCurrent={isCurrent} onPress={onSelect}>
          <S.TextContainer>
            <S.Text
              height={isWeb && inputHeight}
              value={line.line}
              onFocus={onSelect}
              onChangeText={setLine}
              textAlignVertical='top'
              autoCorrect={false}
              scrollEnabled={false}
              onBlur={handleOnBlur}
              onContentSizeChange={handleContentSizeChange}
              multiline
            />
          </S.TextContainer>
          <S.BottomContainer>
            <S.Details>
              <S.DetailsText>{line.startTimeText}</S.DetailsText>
              <S.DetailsText>-</S.DetailsText>
              <S.DetailsText>{line.endTimeText}</S.DetailsText>
            </S.Details>
            {isEdited && (
              <S.Details>
                <S.DetailsText>{t(`${localePrefix}.edited`)}</S.DetailsText>
              </S.Details>
            )}
          </S.BottomContainer>
        </S.Line>
      </ShadowedContainer>
    </S.Container>
  );
};

const S = {
  Container: styled.View`
    margin-top: ${calcHeight(LINE_MARGIN)}px;
  `,
  ShadowedContainerViewStyle: {
    marginLeft: calcWidth(16),
    marginRight: calcWidth(16),
  },
  ShadowedContainerStyle: {
    width: deviceWidth - calcWidth(32),
    borderRadius: 15,
  },
  Line: styled.TouchableOpacity<{ isCurrent: boolean; height: number }>`
    height: ${({ height }) => height}px;
    width: ${deviceWidth - calcWidth(32)}px;
    border: 1px ${({ theme, isCurrent }) => (isCurrent ? theme.colors.primaryBlue : 'transparent')} solid;
    border-radius: 15px;
    background-color: ${({ theme }) => theme.colors.white};
  `,
  TextContainer: styled.View`
    flex-grow: 1;
    padding: ${calcHeight(IS_ANDROID ? 0 : IS_IOS ? 4 : 8)}px ${calcHeight(16)}px;
  `,
  Text: styled.TextInput<{ height?: number }>`
    font-size: ${calcFontSize(20)}px;
    color: ${({ theme }) => theme.colors.darkGray15};
    line-height: ${calcHeight(IS_ANDROID ? 28 : IS_IOS ? 30 : 34)}px;
    width: 100%;
    height: ${({ height }) => (height ? `${height}px` : 'auto')};
  `,
  BottomContainer: styled.View`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${calcHeight(6)}px ${calcWidth(18)}px ${calcHeight(16)}px;
    width: 100%;
  `,
  Details: styled.View`
    flex-direction: row;
    align-items: center;
  `,
  DetailsIcon: styled.View``,
  DetailsText: styled.Text`
    margin: 0px ${calcHeight(4)}px;
    padding-bottom: 2px;
    font-size: ${calcFontSize(14)}px;
    color: ${({ theme }) => theme.colors.darkGray15};
  `,
  Divider: styled(Divider)`
    width: 100%;
    background-color: ${({ theme }) => theme.colors.lightGray33};
  `,
};

export default Line;
